import React from "react";

/**
 * Presentation helpers shared by the chat bubbles and composer. Separate
 * file so component files export only components (react-refresh rule).
 */

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/g;

// Roam posts message text as GFM, so a URL pasted there arrives as a markdown
// link — `[https://app.retail-circle.io/](https://app.retail-circle.io/)`.
// Rendering that raw showed the address TWICE with brackets around it
// (staging 2026-09-28), because the bare-URL pass below linkified the label
// and the target separately. Markdown links are therefore resolved first.
const MD_LINK_PATTERN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

const anchor = (href, label, key) => (
  <a
    key={key}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="rc-chat__link"
  >
    {label}
  </a>
);

/** Bare URLs → anchors. Anchor test uses a fresh non-global regex — .test()
 *  on the global URL_PATTERN would carry lastIndex state between calls. */
const linkifyPlain = (text, keyPrefix) =>
  String(text)
    .split(URL_PATTERN)
    .map((part, i) =>
      /^https?:\/\//.test(part) ? anchor(part, part, `${keyPrefix}-link-${i}`) : part
    );

/** Split message text into text + anchor nodes, handling both markdown links
 *  (as Roam sends them) and bare URLs (as the in-app composer sends them). */
export const linkify = (text) => {
  const source = String(text);
  const out = [];
  let cursor = 0;
  let match;

  MD_LINK_PATTERN.lastIndex = 0;

  while ((match = MD_LINK_PATTERN.exec(source)) !== null) {
    if (match.index > cursor) {
      out.push(...linkifyPlain(source.slice(cursor, match.index), `pre-${match.index}`));
    }

    const [, label, href] = match;
    out.push(anchor(href, label, `md-${match.index}`));
    cursor = match.index + match[0].length;
  }

  if (cursor < source.length) {
    out.push(...linkifyPlain(source.slice(cursor), `tail-${cursor}`));
  }

  return out;
};

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
